package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier

enum class TooltipPlacement(internal val tipClass: String) {
    Top("sb-tip-top"),
    Bottom("sb-tip-bottom"),
    Left("sb-tip-left"),
    Right("sb-tip-right"),
}

private const val HOST_CLASS = "sb-tooltip-host"
private const val TIP_CLASS = "sb-tip"

val TooltipStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("position", "relative")
            property("display", "inline-flex")
        }
    }
}

@Composable
fun Tooltip(
    text: String,
    placement: TooltipPlacement = TooltipPlacement.Top,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Box(modifier = TooltipStyle.toModifier().classNames(HOST_CLASS).then(modifier)) {
        content()
        TooltipBubble(text = text, placement = placement, open = false)
    }
}

@Composable
internal fun TooltipBubble(
    text: String,
    placement: TooltipPlacement,
    open: Boolean,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = Modifier
            .role("tooltip")
            .styleModifier {
                property("position", "absolute")
                property("width", "max-content")
                property("pointer-events", "none")
                property("white-space", "nowrap")
                property("z-index", "${ZIndex.tooltip}")
                applyPlacement(placement)
            }
            .then(modifier),
    ) {
        val bubble = Modifier
            .padding(leftRight = Spacing.sm, topBottom = Spacing.xs)
            .backgroundColor(Palette.surface)
            .color(Palette.textPrimary)
            .borderRadius(Radius.md)
            .fontSize(Typography.bodySmall.size)
            .classNames(TIP_CLASS, placement.tipClass)
            .styleModifier {
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("box-shadow", Shadow.MD)
            }
        Box(modifier = if (open) bubble.classNames(Motion.OPEN_CLASS) else bubble) {
            SpanText(text = text)
        }
    }
}

private fun org.jetbrains.compose.web.css.StyleScope.applyPlacement(placement: TooltipPlacement) {
    val clearance = "calc(100% + ${Spacing.xs})"
    when (placement) {
        TooltipPlacement.Top -> {
            property("bottom", clearance)
            property("left", "50%")
            property("transform", "translateX(-50%)")
        }
        TooltipPlacement.Bottom -> {
            property("top", clearance)
            property("left", "50%")
            property("transform", "translateX(-50%)")
        }
        TooltipPlacement.Left -> {
            property("right", clearance)
            property("top", "50%")
            property("transform", "translateY(-50%)")
        }
        TooltipPlacement.Right -> {
            property("left", clearance)
            property("top", "50%")
            property("transform", "translateY(-50%)")
        }
    }
}
